<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Tooltip } from '@sveltestrap/sveltestrap';

  import { getNotificationsContext } from '@client/components/notifications';
  import { Porthole } from '@client/components/porthole';
  import { SectionItem } from '@client/components/section';
  import { Select } from '@client/components/select';
  import { Spinner } from '@client/components/spinner';

  import { UsersService } from '@client/services/users';

  import { appStore } from '@client/stores/app';
  import { userStore } from '@client/stores/user';

  import { userMetadata } from '@client/utils/users';

  import { USER_ROLES } from '@client/enums';
  import { USER_REQUEST_PARAMS } from '@client/enums';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const ALL_USERS_VALUE = '__all_users__';
  const ALL_USERS_OPTION = {
    label: 'All Users',
    value: ALL_USERS_VALUE,
    isAllOption: true
  };

  let users = [];
  let selectItems = [];
  let selectedUsers = [];
  let loading = false;
  let filterText = '';

  $: allUsersSelected = selectedUsers?.some((u) => u.value === ALL_USERS_VALUE);
  $: nextDisabled = !selectedUsers?.length || loading;

  const onChange = ({ detail }) => {
    selectedUsers = detail || [];
  };

  const onClear = ({ detail }) => {
    if (detail?.length || !selectedUsers?.length) {
      selectedUsers = [];
    } else {
      selectedUsers = selectedUsers?.filter(({ value }) => value !== detail.value) || [];
    }
  };

  const processUsers = (data) => {
    return data.map((user) => {
      const username = user.email.split('@').shift();
      const meta = userMetadata({ ...user, features: $userStore.features });

      return {
        ...user,
        ...meta,
        username,
        label: meta.name,
        value: user.email
      };
    });
  };

  const getUsers = async () => {
    loading = true;
    try {
      const response = await UsersService.getUsers({
        ...USER_REQUEST_PARAMS,
        filterByDate: false,
        perPage: 500,
        hideRemoved: true,
        roles: ['billing', 'domainadmin', 'marketing', 'signatures']
      });

      users = processUsers(response?.data || []).sort((a, b) => a.name.localeCompare(b.name));
      selectItems = [ALL_USERS_OPTION, ...users];
    } catch (error) {
      notify($format('label.FAILURE.LOAD_USERS'), 'error');
    } finally {
      loading = false;
    }
  };

  const executeAction = async () => {
    const targets = allUsersSelected ? users : selectedUsers;

    if (!targets || targets.length === 0) {
      return { success: false };
    }

    $appStore.loading = true;
    const results = [];
    const errors = [];

    try {
      for (const user of targets) {
        try {
          const userData = await UsersService.getUserById(user.value);

          if (!userData) {
            errors.push({ email: user.value, name: user.label, error: 'User not found' });
            continue;
          }

          const response = await UsersService.updateUser({
            ...userData,
            roles: { user: true },
            params: [...(userData.params || [])],
            usersettings: userData.usersettings || {},
            removed: userData.removed || false,
            updateType: 'params'
          });

          if (response?.status === 400) {
            errors.push({ email: user.value, name: user.label, error: response.message });
          } else {
            results.push({
              email: user.value,
              name: user.label,
              success: true,
              previousRoles: userData.roles || {}
            });
          }
        } catch (error) {
          errors.push({ email: user.value, name: user.label, error: error.message });
        }
      }

      if (errors.length > 0) {
        notify($format('label.FAILURE.UPDATE_USERS', { values: { count: errors.length } }), 'error');
      }

      if (results.length > 0) {
        notify(`Admin roles removed from ${results.length} user${results.length !== 1 ? 's' : ''}`);
      }

      step.data = {
        ids: results.map((r) => r.email),
        rollbackData: {
          results,
          errors,
          successCount: results.length,
          errorCount: errors.length
        }
      };

      return { success: errors.length === 0 };
    } catch (error) {
      notify($format('label.FAILURE.UPDATE_USERS'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  const rollbackAction = async () => {
    if (!step.data?.rollbackData?.results) return;

    $appStore.loading = true;

    try {
      for (const result of step.data.rollbackData.results) {
        if (result.success && result.previousRoles) {
          const userData = await UsersService.getUserById(result.email);

          if (userData) {
            await UsersService.updateUser({
              ...userData,
              roles: result.previousRoles,
              params: [...(userData.params || [])],
              usersettings: userData.usersettings || {},
              removed: userData.removed || false,
              updateType: 'params'
            });
          }
        }
      }

      notify($format('label.SUCCESS.ROLLBACK'));
    } catch (error) {
      notify($format('label.FAILURE.ROLLBACK'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  onMount(() => {
    getUsers();

    if (onExecute) {
      onExecute(executeAction);
    }

    if (onRollback) {
      onRollback(rollbackAction);
    }
  });
</script>

<div class="user-remove-admin-roles-form">
  {#if loading}
    <div class="loading">
      <Spinner show inline />
    </div>
  {:else}
    <SectionItem
      label="Remove Admin Roles"
      description="Select the users whose admin roles should be removed. Choose <b>All Users</b> to apply to all users."
    >
      <Select
        id="user-remove-admin-roles-select"
        multiple
        searchable
        clearable
        placeholder={$format('label.SELECT_USERS')}
        bind:items={selectItems}
        bind:value={selectedUsers}
        bind:filterText
        on:change={onChange}
        on:clear={onClear}
        disabled={loading}
      >
        <svelte:fragment slot="item" let:item>
          {#if item.isAllOption}
            <div class="item all-users-item">
              <div class="all-users-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                  <path
                    d="M15 14s1 0 1-1-1-4-5-4-5 3-5 4 1 1 1 1zm-7.978-1A.261.261 0 0 1 7 12.996c.001-.264.167-1.03.76-1.72C8.312 10.629 9.282 10 11 10c1.717 0 2.687.63 3.24 1.276.593.69.758 1.457.76 1.72l-.008.002-.014.002H7.022zM11 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4m3-2a3 3 0 1 1-6 0 3 3 0 0 1 6 0M6.936 9.28a5.88 5.88 0 0 0-1.23-.247A7.35 7.35 0 0 0 5 9c-4 0-5 3-5 4 0 .667.333 1 1 1h4.216A2.238 2.238 0 0 1 5 13c0-1.01.377-2.042 1.09-2.904.243-.294.526-.569.846-.816M4.92 10A5.493 5.493 0 0 0 4 13H1c0-.26.164-1.03.76-1.724.545-.636 1.492-1.256 3.16-1.275ZM1.5 5.5a3 3 0 1 1 6 0 3 3 0 0 1-6 0m3-2a2 2 0 1 0 0 4 2 2 0 0 0 0-4"
                  />
                </svg>
              </div>
              <div class="item-detail">
                <div class="name all-users-label">All Users</div>
                <div class="email">Remove admin roles from all {users.length} users</div>
              </div>
            </div>
          {:else}
            <div class="item">
              {#key item}
                <Porthole details={item} />
              {/key}
              <div class="item-detail">
                <div class="name-row">
                  <div class="name">{item.name}</div>
                  <div class="roles">
                    {#each Object.entries(item.roles || {}).sort() as [role, active]}
                      {#if active && USER_ROLES[role]}
                        {@const roleId = `rar-${item.email.replace(/[@.]/g, '-')}-${role}`}
                        <div id={roleId} class="role" style="background-color: {USER_ROLES[role].color};" />
                        <Tooltip target={roleId} placement="top">
                          {$format(USER_ROLES[role].label.long)}
                        </Tooltip>
                      {/if}
                    {/each}
                  </div>
                </div>
                <div class="email">{item.email}</div>
              </div>
            </div>
          {/if}
        </svelte:fragment>
        <div slot="empty" class="empty">
          {$format('label.NO_OPTIONS_AVAILABLE')}
        </div>
      </Select>
    </SectionItem>
  {/if}
</div>

<style lang="scss">
  .user-remove-admin-roles-form {
    .loading {
      align-items: center;
      display: flex;
      justify-content: center;
      padding: var(--padding-large);
    }

    :global(.section-item .row.align-items-center) {
      align-items: flex-start !important;
    }
  }

  .item {
    align-items: center;
    display: grid;
    gap: 16px;
    grid-template-columns: 48px auto;

    .item-detail {
      .name-row {
        align-items: center;
        display: flex;
        gap: 6px;
      }

      .roles {
        align-items: center;
        display: flex;

        > *:not(:first-child) {
          margin-left: -6px;
        }

        &:hover {
          > * {
            margin-left: 0;
          }
        }

        .role {
          border: 2px solid var(--color-white);
          border-radius: 20px;
          height: 14px;
          overflow: hidden;
          transition: all 0.3s cubic-bezier(0.8, -0.5, 0.2, 1.4);
          width: 14px;
        }
      }

      .name {
        font-weight: var(--font-weight-medium);
      }

      .email {
        color: var(--color-gray-600);
        font-size: var(--font-size-medium);
      }
    }

    &.active {
      .item-detail {
        .email {
          color: var(--color-blue-100);
        }
      }
    }
  }

  .all-users-item {
    grid-template-columns: 48px auto;

    .all-users-icon {
      align-items: center;
      background: var(--color-gray-100);
      border-radius: 50%;
      color: var(--color-gray-600);
      display: flex;
      height: 40px;
      justify-content: center;
      width: 40px;
    }

    .all-users-label {
      color: var(--color-gray-900);
    }
  }

  .empty {
    color: var(--color-gray-600);
    padding: var(--padding-xlarge);
    text-align: center;
  }
</style>
